import { ButtonV2, Checkbox, Icon, Link, SelectGroup, TooltipTrigger } from 'components';
import { CORP_PERMISSIONS, PERMISSIONS, SCHOOL_PERMISSIONS, getPermissionMap } from 'constants/permissions';
import useLaunchDarkly from 'hooks/useLaunchDarkly';
import { pickPermissionLevel } from 'lib/utils';
import get from 'lodash/get';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import './style.scss';

const RoleSelection = ({ onClose, currentSchool, corpSchool, currentUser, tenant, data }) => {
  const flags = useLaunchDarkly();
  const initialRole = data.permissionRole || {};
  const roles = data.permissionRoles || [];
  const isViewOnly = data.view;
  const staff = data.staff || null;
  const siteId = data.siteId;

  const [role, setRole] = useState(initialRole);
  const [selectedRoleId, setSelectedRoleId] = useState(initialRole.id);
  const adminLabel = role?.is_admin ? 'Admin' : 'Non-Admin';

  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);

  const permissionMapWithOverrides = getPermissionMap(flags);
  const ALL_PERMISSIONS = (corpSchool ? CORP_PERMISSIONS : PERMISSIONS)
    .filter(key => {
      const hideFlag = permissionMapWithOverrides[key].showPermission;
      return !hideFlag || flags[hideFlag];
    })
    .sort((a, b) => permissionMapWithOverrides[a].label.localeCompare(permissionMapWithOverrides[b].label));

  useEffect(() => {
    if (initialRole.id) fetchRoleById(initialRole.id);
  }, [initialRole.id, fetchRoleById]);

  useEffect(() => {
    setName(role.name || '');
    setIsAdmin(!!role.is_admin);
    const initPerms = {};
    ALL_PERMISSIONS.forEach(key => {
      const arr = role.line_items?.[key] || [];
      initPerms[key] = pickPermissionLevel(arr);
    });
    setPermissions(initPerms);
  }, [role]);

  const handleChangePermission = (key, level) => {
    setPermissions(prev => ({ ...prev, [key]: level }));
  };

  const fetchRoleById = useCallback(
    permissionId => {
      const resolvedSiteId =
        siteId || tenant?.sites?.find(s => s.pom_identifier === `PCO:SITE:${currentUser?.current_school?.id}`)?.id;
      if (!resolvedSiteId || !permissionId) return;
      req
        .fetchRoleById({ site_id: resolvedSiteId, permission_role_id: permissionId })
        .then(result => {
          setRole(result);
        })
        .catch(err => console.error('Error fetching role:', err));
    },
    [currentUser, tenant, siteId]
  );

  const assignRole = useCallback(() => {
    if (!staff?.id) return;
    const resolvedSiteId =
      siteId || tenant?.sites?.find(s => s.pom_identifier === `PCO:SITE:${currentUser?.current_school?.id}`)?.id;
    if (!resolvedSiteId) return;

    const chosen = roles.find(r => r.id === selectedRoleId) || {};
    const existing = chosen.pom_staff_identifiers || [];
    const thisIdentifier = `PCO:STAFF:${staff.id}`;
    const uniqIds = Array.from(new Set([...existing, thisIdentifier]));
    const payload = { assignments: uniqIds.map(id => ({ pom_staff_identifier: id })) };

    setSaving(true);
    req
      .assignStaffToRole({
        site_id: resolvedSiteId,
        permission_role_id: selectedRoleId,
        ...payload
      })
      .then(() => {
        Actions.showFlash(`Role Updated`);
        onClose();
      })
      .catch(err => {
        Actions.reportError('Unable to assign staff to role: ', err);
        setSaving(false);
      });
  }, [staff, siteId, tenant, currentUser, roles, selectedRoleId, onClose]);

  const handleRoleSelect = useCallback(
    nextRoleId => {
      setSelectedRoleId(nextRoleId);
      fetchRoleById(nextRoleId);
    },
    [fetchRoleById]
  );

  const filteredPermissions = ALL_PERMISSIONS.filter(
    key =>
      (!permissionMapWithOverrides[key].enabledBySchoolSetting ||
        permissionMapWithOverrides[key].enabledBySchoolSetting(currentSchool)) &&
      !SCHOOL_PERMISSIONS.includes(key)
  );
  const schoolPermissions = ALL_PERMISSIONS.filter(
    key =>
      (!permissionMapWithOverrides[key].enabledBySchoolSetting ||
        permissionMapWithOverrides[key].enabledBySchoolSetting(currentSchool)) &&
      SCHOOL_PERMISSIONS.includes(key)
  );

  const renderRow = key => {
    const value = permissions[key];
    const { label, tooltip } = permissionMapWithOverrides[key];
    const disabledKeys = get(
      permissionMapWithOverrides,
      `${key}.${corpSchool ? 'disabledCorp' : 'disabled'}.${role.is_admin ? 'admin' : 'nonAdmin'}`,
      []
    );
    return (
      <div key={key} className="role-selection__row">
        <div className="role-selection__row-label">
          {label}
          {tooltip && (
            <TooltipTrigger tooltip={tooltip} side="center" className="role-selection__row-tooltip">
              <ButtonV2 icon iconName="tooltip" iconSize={16} />
            </TooltipTrigger>
          )}
        </div>
        <div className="role-selection__row-values">
          <Checkbox
            checked={value === 'none'}
            disabled={isViewOnly || disabledKeys.includes('none')}
            onChange={() => handleChangePermission(key, 'none')}
            label="No Access"
          />
          <Checkbox
            checked={value === 'read'}
            disabled={isViewOnly || disabledKeys.includes('read')}
            onChange={() => handleChangePermission(key, 'read')}
            label="Read"
          />
          <Checkbox
            checked={value === 'write'}
            disabled={isViewOnly || disabledKeys.includes('write')}
            onChange={() => handleChangePermission(key, 'write')}
            label="Full Access"
          />
        </div>
      </div>
    );
  };

  const isSubmitDisabled = saving || !selectedRoleId || selectedRoleId === initialRole.id;

  return (
    <div className="modal__wrapper role-selection">
      <div className="modal__header modal__header--bordered">
        <div className="modal__header-title">Edit Role</div>
      </div>
      <div className="modal__container">
        <div className="role-selection__content">
          <div className="role-selection__role-name-field">
            <label>Role Selection:</label>
            <SelectGroup
              type="radio"
              title=""
              className="staff-selection"
              checked={selectedRoleId || ''}
              onChange={handleRoleSelect}
              disabled={saving}
            >
              {roles.map(r => (
                <SelectGroup.Item key={r.id} value={r.id} label={r.name} />
              ))}
            </SelectGroup>
          </div>

          <div className="role-admin">
            <div className="role-admin__left">
              <div className="role-name">
                <span>{adminLabel}</span>
                {role?.is_admin && <Icon className="staff-profile__role-icon" name="admin" size="16" />}
              </div>
              <TooltipTrigger
                tooltip="Permissions for this user will be reset with the role change."
                side="right"
                className="admin-type-tooltip"
              >
                <Icon name="tooltip" size={16} />
              </TooltipTrigger>
            </div>

            <div className="role-admin__right">
              <Link onClick={onClose} to={`/settings/roles`}>
                Edit Roles &amp; Permissions
              </Link>
            </div>
          </div>

          <div className="role-selection__section">
            {corpSchool && <div className="role-selection__section-title">Corporate Portal</div>}
            {filteredPermissions.map(renderRow)}
          </div>

          {corpSchool && (
            <div className="role-selection__section">
              <div className="role-selection__section-title">School Permissions</div>
              {schoolPermissions.map(renderRow)}
            </div>
          )}
        </div>

        <div className="form__row form__row--actions">
          <ButtonV2
            label="Save Changes"
            onClick={assignRole}
            data-cy="save"
            isLoading={saving}
            disabled={isSubmitDisabled}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  currentSchool: state.currentUser.data.current_school,
  corpSchool: state.currentUser.data.current_school.corp_school,
  currentUser: state.currentUser.data,
  tenant: state.tenant.data
});

export default connect(mapStateToProps)(RoleSelection);
